import T from 'i18n-react';
import moment from 'moment';
import isArray from 'lodash/isArray';

import { ISSUE_FILTER_PROPERTIES } from '../constants/issue-query-params';

const RFI_REPORT_SORT_IDS = {
  title: 'title',
  due_date: 'dueDate',
  identifier: 'customIdentifier',
  custom_identifier: 'customIdentifier',
  status: 'status',
  location_description: 'location',
};

const RFI_REPORT_FILTERS = {
  id: { field: 'id', operator: 'in', logic: 'AND' },
  status: { field: 'status', operator: 'in', logic: 'AND' },
  assignedTo: { field: 'assignedTo', operator: 'in', logic: 'AND' },
  createdAt: { field: 'createdAt', operator: 'BETWEEN', logic: 'AND', dataType: 'datetime' },
  createdBy: { field: 'createdBy', operator: 'in', logic: 'AND' },
  dueDate: { field: 'dueDate', operator: 'BETWEEN', logic: 'AND', dataType: 'datetime' },
};

const parseQueryParams = (queryParams) => {
  const {
    STATUS_FILTER,
    ASSIGNED_TO_FILTER,
    CREATED_BY_FILTER,
    CREATED_AT_START_FILTER,
    CREATED_AT_END_FILTER,
    DUE_DATE_START_FILTER,
    DUE_DATE_END_FILTER,
  } = ISSUE_FILTER_PROPERTIES;

  const hasCreatedAtFilter =
    queryParams[CREATED_AT_START_FILTER] && queryParams[CREATED_AT_END_FILTER];
  const hasDueDateFilter = queryParams[DUE_DATE_START_FILTER] && queryParams[DUE_DATE_END_FILTER];

  return {
    filters: {
      ...(queryParams[STATUS_FILTER] && { status: queryParams[STATUS_FILTER] }),
      ...(queryParams[ASSIGNED_TO_FILTER] && { assignedTo: queryParams[ASSIGNED_TO_FILTER] }),
      ...(queryParams[CREATED_BY_FILTER] && { createdBy: queryParams[CREATED_BY_FILTER] }),
      ...(hasCreatedAtFilter && {
        createdAt: [queryParams[CREATED_AT_START_FILTER], queryParams[CREATED_AT_END_FILTER]],
      }),
      ...(hasDueDateFilter && {
        dueDate: [queryParams[DUE_DATE_START_FILTER], queryParams[DUE_DATE_END_FILTER]],
      }),
    },
    sort: queryParams.sort,
  };
};

export const getSortOption = (sortString) => {
  if (sortString) {
    const isDescending = sortString[0] === '-';
    const sortId = RFI_REPORT_SORT_IDS[isDescending ? sortString.slice(1) : sortString];
    const sortOrder = isDescending ? 'DESC' : 'ASC';

    return sortId ? [`${sortId} ${sortOrder}`] : [];
  }

  return [];
};

const getFilterValue = ({ dataType, values }) => {
  if (dataType === 'datetime') {
    if (values[0] && values[1]) {
      const startDateTime = moment(values[0]).startOf('day').toISOString();
      const endDateTime = moment(values[1]).endOf('day').toISOString();

      return `${startDateTime}...${endDateTime}`;
    }

    return null;
  }

  return isArray(values) ? values.join(',') : values;
};

export const getFilterOptions = (filters) => {
  if (filters) {
    return Object.entries(filters)
      .map(([label, values]) => {
        const filterDefinition = RFI_REPORT_FILTERS[label];

        if (filterDefinition) {
          const { dataType, ...filter } = filterDefinition;
          const value = getFilterValue({ dataType, values });

          if (value) {
            return { ...filter, value };
          }

          return null;
        }

        return null;
      })
      .filter(Boolean);
  }

  return [];
};

export const getRfiSummaryPayload = ({ title, message, queryParams, format = 'pdf' }) => {
  const { filters, sort } = parseQueryParams(queryParams);

  return {
    service: 'rfis',
    template: 'summary',
    title,
    format,
    message,
    options: {
      sort: getSortOption(sort),
      filter: getFilterOptions(filters),
    },
  };
};

export const getRfiDetailPayload = (model) => {
  const filters = { id: [model.id] };
  const labelText = T.translate('rfis.label_capitalized');
  const title = `${labelText} #${model.customIdentifier || T.translate('unspecifiedValue')}: ${
    model.title
  }`;

  return {
    service: 'rfis',
    template: 'detail',
    title,
    format: 'pdf',
    options: {
      sort: [],
      filter: getFilterOptions(filters),
    },
  };
};
